import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { opportunityAPI } from "@/services/opportunityService";

export const opportunityKeys = {
  all: ["opportunities"] as const,
  lists: () => [...opportunityKeys.all, "list"] as const,
  list: (filters: string) => [...opportunityKeys.lists(), { filters }] as const,
  details: () => [...opportunityKeys.all, "detail"] as const,
  detail: (id: string) => [...opportunityKeys.details(), id] as const,
  stages: (pipelineId: string) => ["opportunityStages", pipelineId] as const,
};

export function useGetOpportunities(params: any) {
  return useQuery({
    queryKey: opportunityKeys.list(JSON.stringify(params)),
    queryFn: async () => {
      const response = await opportunityAPI.getOpportunities(params);
      // Retornar la respuesta completa para tener acceso a los metadatos de paginación
      return response;
    },
    // Solo se ejecuta si hay un slug válido
    enabled: !!params.slug,
    staleTime: 1000 * 60 * 2, // 2 minutos sin re-fetch automático
  });
}

export function useGetOpportunityStages(slug: string, pipelineId: string) {
  return useQuery({
    queryKey: opportunityKeys.stages(pipelineId),
    queryFn: async () => {
      const response = await opportunityAPI.getStages({
        slug,
        pipeline_id: pipelineId,
      });
      return response.data
        .filter((s: any) => s.pipeline_id === pipelineId)
        .sort((a: any, b: any) => a.order_position - b.order_position);
    },
    enabled: !!slug && !!pipelineId,
    staleTime: 1000 * 60 * 5, // 5 minutos, las etapas casi no cambian
  });
}

// Hook para actualizar una etapa (Sin Optimistic Update agresivo por seguridad)
export function useUpdateOpportunityStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      stageId,
      slug,
      reason,
    }: {
      id: string;
      stageId: string;
      slug: string;
      reason?: string;
    }) => {
      return opportunityAPI.updateOpportunity(id, { stage_id: stageId, slug, reason } as any);
    },
    onSuccess: () => {
      // Invalida inmediatamente para recargar de la base de datos la verdad absoluta
      queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}
